import { inject, Injectable, signal } from '@angular/core';
import { from, race, timer, throwError, Observable, ReplaySubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IAuthTokenStorageService, TokenData } from './auth.model';
import { FEATURE_NAME, COOKIE_DOMAIN, COOKIE_NAME } from './auth.constant';
import { HttpStatusCodes } from '@zerops/zef/core';

// # Token Storage Service

@Injectable({ providedIn: 'root' })
export class AuthTokenStorageService implements IAuthTokenStorageService {
  #cookieDomain = inject(COOKIE_DOMAIN, { optional: true }) ?? '';
  #data: TokenData;
  private _sessionUserKey = '@zerops/zef/session-user';
  private _sessionClientUserKey = '@zerops/zef/session-client-user';

  sessionInvalidated = signal(false);
  sessionInvalidReason = signal<'logout' | 'userChanged' | 'clientChanged' | null>(null);
  crossTabSessionInvalidated$ = new ReplaySubject<void>(1);

  // Guard against Safari firing StorageEvent in the same tab that wrote the value.
  // Set before write, cleared after the event loop processes the synchronous event.
  private _selfWrite = false;

  setToken(data: TokenData) {
    this.#data = data;
    this.#updateStorage();
    // Track user per-tab so boot flow can detect cross-tab user changes
    if (data?.userId && !requires2FA(data)) {
      sessionStorage.setItem(this._sessionUserKey, data.userId);
    }
    // Only set login cookie if token is fully authenticated (not partial 2FA token)
    if (!requires2FA(data)) {
      this.#setLoginCookie();
    }
  }

  getSessionUserId(): string | null {
    return sessionStorage.getItem(this._sessionUserKey);
  }

  setSessionUserId(userId: string) {
    sessionStorage.setItem(this._sessionUserKey, userId);
  }

  getSessionClientUserId(): string | null {
    return sessionStorage.getItem(this._sessionClientUserKey);
  }

  setSessionClientUserId(clientUserId: string) {
    sessionStorage.setItem(this._sessionClientUserKey, clientUserId);
  }

  removeToken() {
    this.#data = undefined;
    this.#updateStorage();
    this.#clearLoginCookie();
  }

  getToken() {
    return this.#data;
  }

  /**
   * Validates if the stored token is fully authenticated and ready for use.
   *
   * A token is considered valid only if:
   * 1. Token data exists
   * 2. Access token is present
   * 3. Token does not require 2FA verification (partial tokens are invalid)
   *
   * This prevents partial 2FA tokens from being treated as fully authenticated
   * on page refresh, which would cause blank pages and failed API calls.
   *
   * @returns true if token is fully authenticated and usable
   */
  isValid() {
    return !!(this.#data && this.#data.accessToken && !requires2FA(this.#data));
  }

  constructor() {
    this.#data = this.#getStorage();
    // Refresh cookie on page load if valid token exists (extends cookie life)
    if (this.isValid()) {
      this.#setLoginCookie();
    }

    window.addEventListener('storage', (event) => {
      if (event.key !== FEATURE_NAME) {
        return;
      }

      // Already invalidated by a previous storage event — ignore further changes
      // to prevent dialog text flicker and redundant cleanup effect dispatches
      if (this.sessionInvalidated()) {
      	return;
      }

      // Safari fires storage events in the same tab that wrote the value.
      // Ignore these to prevent logout loops.
      if (this._selfWrite) {
        return;
      }

      // Token removed in another tab (logout)
      if (!event.newValue) {
        this.sessionInvalidated.set(true);
        this.sessionInvalidReason.set('logout');
        this.crossTabSessionInvalidated$.next();
        return;
      }

      try {
        const parsed: TokenData = JSON.parse(event.newValue);

        // Different user logged in in another tab
        if (this.#data && parsed.userId && this.#data.userId !== parsed.userId) {
          this.sessionInvalidated.set(true);
          this.sessionInvalidReason.set('userChanged');
          this.crossTabSessionInvalidated$.next();
          return;
        }

        // Same user, token refreshed — update in-memory cache
        this.#data = parsed;
      } catch {
        // Ignore malformed data
      }
    });

    // Recovery: auto-reload when same session is restored in another tab
    window.addEventListener('storage', () => {
      if (this.sessionInvalidated() && !this._selfWrite && this.#isSameSessionRestored()) {
        window.location.reload();
      }
    });
  }

  #isSameSessionRestored(): boolean {
    try {
      const token = this.#getStorage();
      if (!token?.accessToken || requires2FA(token)) return false;

      const userDataRaw = localStorage.getItem('@zerops/zerops/user-data');
      if (!userDataRaw) return false;
      const userData = JSON.parse(userDataRaw);

      return token.userId === this.getSessionUserId()
        && userData.clientUserId === this.getSessionClientUserId();
    } catch {
      return false;
    }
  }

  #updateStorage() {
    const d = this.#data
      ? JSON.stringify(this.#data)
      : '';

    this._selfWrite = true;
    localStorage.setItem(FEATURE_NAME, d);
    this._selfWrite = false;
  }

  #getStorage() {
    const s = localStorage.getItem(FEATURE_NAME);

    return s ? JSON.parse(s) : undefined;
  }

  /**
   * Sets the cross-app login detection cookie.
   * This cookie allows other apps (like web.zerops.io) to detect
   * that the user is logged into the main app.
   */
  #setLoginCookie(): void {
    if (typeof document === 'undefined') return; // SSR guard

    const domain = this.#resolveCookieDomain();
    const isSecure = window.location.protocol === 'https:';
    const secureFlags = isSecure ? '; Secure; SameSite=Lax' : '';
    const domainPart = domain ? `; domain=${domain}` : '';

    document.cookie = `${COOKIE_NAME}=true; path=/; max-age=86400${domainPart}${secureFlags}`;
  }

  /**
   * Clears the cross-app login detection cookie.
   */
  #clearLoginCookie(): void {
    if (typeof document === 'undefined') return; // SSR guard

    const domain = this.#resolveCookieDomain();
    const isSecure = window.location.protocol === 'https:';
    const secureFlags = isSecure ? '; Secure; SameSite=Lax' : '';

    // Clear with domain if configured
    if (domain) {
      document.cookie = `${COOKIE_NAME}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT${secureFlags}`;
    }
    // Also clear without domain for localhost/development
    document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${secureFlags}`;
  }

  /**
   * Resolves the cookie domain based on configuration and current hostname.
   * Returns empty string for localhost/IP addresses (browser default behavior).
   */
  #resolveCookieDomain(): string {
    const hostname = window.location.hostname;

    // If explicitly configured, use that
    if (this.#cookieDomain) {
      return this.#cookieDomain;
    }

    // localhost/IP addresses - don't set domain (use browser default)
    if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return '';
    }

    // For subdomains, use parent domain (e.g., app.zerops.io -> .zerops.io)
    const parts = hostname.split('.');
    if (parts.length > 2) {
      return '.' + parts.slice(-2).join('.');
    }

    return '.' + hostname;
  }
}

// # Sudo Utils

export const SUDO_ERROR_CODE = 'notInSudoMode';
export const SUDO_ERROR_KEY = 'sudo-required';

/**
 * Checks if an error is a sudo-required error.
 * Returns true if the error indicates sudo mode is required.
 *
 * Handles errors from multiple sources:
 * - Normalized errors from ErrorsInterceptor (have error.code directly)
 * - Raw HTTP errors (need to check nested error.originalError.error.error.code)
 */
export function isSudoRequiredError(error: any): boolean {
  if (!error) {
    return false;
  }

  // Check normalized error format (from ErrorsInterceptor)
  if (error.code === SUDO_ERROR_CODE) {
    return true;
  }

  // Check nested error format (before normalization or from different source)
  // This handles: error.originalError.error.error.code
  if (error.originalError?.error?.error?.code === SUDO_ERROR_CODE) {
    return true;
  }

  // Additional check for 403 status to catch any sudo errors
  if (error.status === HttpStatusCodes.FORBIDDEN) {
    // Recheck nested path for 403 errors
    if (error.originalError?.error?.error?.code === SUDO_ERROR_CODE) {
      return true;
    }
  }

  return false;
}

// # 2FA Utils

/**
 * Checks if a token requires 2FA verification.
 * Returns true if 2FA methods are present but verification is not complete.
 *
 * A token requires 2FA when:
 * - Token data exists
 * - twoFAMethods array is present and non-empty
 * - twoFAVerified flag is not explicitly true
 *
 * This function is critical for preventing security issues and UX bugs:
 *
 * **Security**: Partial 2FA tokens should never be used for API calls.
 * They represent an incomplete authentication flow and must not grant access
 * to protected resources.
 *
 * **Bug Prevention**: When a user refreshes the page during 2FA login,
 * the partial token is stored in localStorage. Without this check, the app would:
 * 1. Treat the partial token as fully authenticated
 * 2. Attempt to load user data with an unverified token
 * 3. Fail to load user entity → null activeClientUserId
 * 4. Permission guards fail → router can't navigate
 * 5. Result: BLANK PAGE 🔴
 *
 * This utility is used in:
 * - `AuthTokenStorageService.isValid()` - Validates stored tokens on app boot
 * - `app.effect.ts` - Guards against loading resources with partial tokens
 * - `login.effect.ts` - Detects when to show 2FA prompt
 *
 * @param tokenData The token data to check (can be null/undefined)
 * @returns true if token is partial and requires 2FA completion
 *
 * @example
 * ```typescript
 * // In token validation
 * if (requires2FA(tokenData)) {
 *   // Invalid - don't proceed with this token
 *   return false;
 * }
 *
 * // In effects
 * filter(({ data }) => !requires2FA(data)), // Skip partial tokens
 *
 * // In login flow
 * if (requires2FA(data)) {
 *   // Show 2FA prompt UI
 *   return authActions.twoFAPromptRequired({ methods: data.twoFAMethods });
 * }
 * ```
 */
export function requires2FA(tokenData: TokenData): boolean {
  return !!(tokenData && tokenData.twoFAMethods && tokenData.twoFAMethods.length > 0 && tokenData.twoFAVerified !== true);
}

// # WebAuthn Utils

/**
 * Unwraps WebAuthn challenge response from backend.
 * Backend may return either wrapped format { publicKey: {...} } or unwrapped format directly.
 * This utility handles both cases consistently and validates the result.
 *
 * @param response The WebAuthn response from the backend
 * @returns The unwrapped challenge options ready for use with @simplewebauthn/browser
 * @throws Error if the response is invalid or missing required fields
 */
export function unwrapWebAuthnChallenge(response: any): any {
  // Backend may wrap in { publicKey: {...} }
  const options = response?.publicKey ?? response;

  if (!options || typeof options !== 'object') {
    throw new Error('[WebAuthn] Invalid challenge format - expected object');
  }

  if (!options.challenge) {
    throw new Error('[WebAuthn] Challenge missing from options');
  }

  return options;
}

/**
 * Options for WebAuthn authentication
 */
export interface WebAuthnAuthenticationOptions {
  /** Challenge response from the backend */
  challenge: any;
  /** Timeout in milliseconds (default: 60000) */
  timeout?: number;
  /** Enable browser autofill for passkeys */
  useBrowserAutofill?: boolean;
}

/**
 * Performs WebAuthn authentication with timeout and optional autofill.
 * Handles dynamic import of @simplewebauthn/browser and provides consistent error handling.
 *
 * Note: @simplewebauthn/browser manages abort internally - no need for external AbortController.
 * Observable unsubscription (via switchMap/takeUntil at call site) will cancel the operation.
 *
 * @param options Configuration for the authentication
 * @returns Observable that emits the WebAuthn authentication response
 *
 * @example
 * ```typescript
 * performWebAuthnAuthentication({
 *   challenge: challengeData,
 *   timeout: 60000,
 *   useBrowserAutofill: true
 * }).pipe(
 *   map((response) => loginSuccess({ response })),
 *   catchError((error) => of(loginFailure(error)))
 * )
 * ```
 */
export function performWebAuthnAuthentication(
  options: WebAuthnAuthenticationOptions
): Observable<any> {
  const { challenge, timeout = 60000, useBrowserAutofill = false } = options;
  const webAuthnOptions = unwrapWebAuthnChallenge(challenge);

  return from(import('@simplewebauthn/browser')).pipe(
    switchMap(({ startAuthentication }) =>
      race([
        from(startAuthentication({
          optionsJSON: webAuthnOptions,
          useBrowserAutofill
        })),
        timer(timeout).pipe(
          switchMap(() => throwError(() =>
            new Error(`WebAuthn authentication timed out after ${timeout / 1000}s`)
          ))
        )
      ])
    )
  );
}

/**
 * Options for WebAuthn registration
 */
export interface WebAuthnRegistrationOptions {
  /** Registration options from the backend */
  registrationOptions: any;
  /** Timeout in milliseconds (default: 60000) */
  timeout?: number;
}

/**
 * Performs WebAuthn registration with timeout.
 * Handles dynamic import of @simplewebauthn/browser and provides consistent error handling.
 *
 * @param options Configuration for the registration
 * @returns Observable that emits the WebAuthn registration response
 *
 * @example
 * ```typescript
 * performWebAuthnRegistration({
 *   registrationOptions: options,
 *   timeout: 60000
 * }).pipe(
 *   map((response) => registrationSuccess({ response })),
 *   catchError((error) => of(registrationFailure(error)))
 * )
 * ```
 */
export function performWebAuthnRegistration(
  options: WebAuthnRegistrationOptions
): Observable<any> {
  const { registrationOptions, timeout = 60000 } = options;
  const webAuthnOptions = unwrapWebAuthnChallenge(registrationOptions);

  return from(import('@simplewebauthn/browser')).pipe(
    switchMap(({ startRegistration }) =>
      race([
        from(startRegistration(webAuthnOptions)),
        timer(timeout).pipe(
          switchMap(() => throwError(() =>
            new Error(`WebAuthn registration timed out after ${timeout / 1000}s`)
          ))
        )
      ])
    )
  );
}
